/*
 * Champions Coffee theme script.
 *
 * The Next.js site's client behaviour (motion/react, the cart context, the header, search and the
 * forms) rewritten as small vanilla modules that attach to data attributes in the Liquid markup.
 * No framework and no build step: this file is served as it is.
 *
 *   data-reveal / data-reveal-delay         fade + lift on scroll (Reveal / SplitWords)
 *   data-marquee                            scroll-velocity band (Marquee)
 *   canvas[data-steam]                      rising steam (Steam)
 *   [data-header]                           header, mega menu and mobile menu (Header)
 *   [data-scroll-buttons]                   back to top / scroll down (ScrollButtons)
 *   [data-cart-open], form[data-product-form], [data-quick-add]   Shopify AJAX cart + drawer
 *   [data-search-open]                      search dialog with Shopify predictive search
 *   form[data-lead-form]                    lead forms stored as Shopify customers (see FORMS.md)
 *   [data-nudge]                            carousel arrows (scroll a track by 80% of its width)
 *
 * Every module also re-runs inside a section the theme editor reloads (shopify:section:load).
 */
(() => {
  "use strict";

  const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const routes = (window.Champions && window.Champions.routes) || { root: "/", cart: "/cart", cartAdd: "/cart/add", cartChange: "/cart/change", predictiveSearch: "/search/suggest", search: "/search" };
  const join = (base, path) => base.replace(/\/$/, "") + path;

  /* Page scroll lock shared by the drawers and dialogs (a counter, so two overlays can stack). */
  let locks = 0;
  const lockScroll = (on) => {
    locks = Math.max(0, locks + (on ? 1 : -1));
    document.documentElement.style.overflow = locks ? "hidden" : "";
  };

  /* Focus management for overlays: remember what had focus, trap Tab inside, restore on close. */
  const focusables = (root) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', root).filter((el) => el.offsetParent !== null || el === document.activeElement);
  const trap = (root, e) => {
    if (e.key !== "Tab") return;
    const f = focusables(root);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  function makeOverlay(el, { onOpen, onClose } = {}) {
    if (!el || el.__overlay) return el && el.__overlay;
    let lastFocus = null;
    const isOpen = () => el.getAttribute("aria-hidden") === "false";
    const api = {
      open() {
        if (isOpen()) return;
        lastFocus = document.activeElement;
        el.setAttribute("aria-hidden", "false");
        el.removeAttribute("inert");
        lockScroll(true);
        onOpen && onOpen();
        const auto = el.querySelector("[autofocus], [data-autofocus]") || focusables(el)[0];
        auto && setTimeout(() => auto.focus(), 30);
      },
      close() {
        if (!isOpen()) return;
        el.setAttribute("aria-hidden", "true");
        el.setAttribute("inert", "");
        lockScroll(false);
        onClose && onClose();
        if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
      },
      isOpen,
    };
    el.addEventListener("keydown", (e) => {
      if (e.key === "Escape") api.close();
      else trap(el, e);
    });
    el.addEventListener("click", (e) => {
      if (e.target.closest("[data-overlay-close]")) api.close();
    });
    if (!isOpen()) el.setAttribute("inert", "");
    el.__overlay = api;
    return api;
  }

  /* ------------------------------------------------------------------ reveal */
  // Same rule as the React <Reveal>: 0.4 s, delays capped at 0.15 s, once, 8% bottom margin.
  let revealObserver;
  function initReveal(root = document) {
    const items = $$("[data-reveal]", root).filter((el) => !el.classList.contains("is-revealed"));
    if (!items.length) return;
    items.forEach((el) => {
      const d = Math.min(parseFloat(el.dataset.revealDelay || "0") || 0, 0.15);
      if (d) el.style.setProperty("--reveal-delay", `${d}s`);
      if (el.dataset.revealY) el.style.setProperty("--reveal-y", `${Math.min(parseFloat(el.dataset.revealY), 12)}px`);
    });
    if (reduceMotion() || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add("is-revealed");
            revealObserver.unobserve(e.target);
          });
        },
        { rootMargin: "0px 0px -8% 0px" },
      );
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ------------------------------------------------------------------ marquee */
  // Endless band whose speed and direction follow scroll velocity (Marquee.tsx).
  function initMarquee(root = document) {
    $$("[data-marquee]", root).forEach((band) => {
      if (band.__marquee) return;
      band.__marquee = true;
      const track = band.querySelector("[data-marquee-track]");
      if (!track) return;
      const repeat = track.children.length || 4;
      const speed = parseFloat(band.dataset.speed || "2.2");
      let base = 0;
      let dir = 1;
      let inView = false;
      let lastY = window.scrollY;
      let lastT = performance.now();
      let velocity = 0;
      let smooth = 0;
      new IntersectionObserver(([e]) => (inView = e.isIntersecting), { rootMargin: "20% 0px" }).observe(band);
      const wrap = (min, max, v) => {
        const r = max - min;
        return ((((v - min) % r) + r) % r) + min;
      };
      const frame = (t) => {
        const dt = Math.min(t - lastT, 64);
        lastT = t;
        if (!reduceMotion() && inView) {
          const y = window.scrollY;
          velocity = dt ? ((y - lastY) / dt) * 1000 : 0;
          lastY = y;
          // spring-ish smoothing (damping 50, stiffness 300 in the React version)
          smooth += (velocity - smooth) * Math.min(1, dt / 90);
          const factor = (smooth / 1000) * 4;
          let move = dir * speed * (dt / 1000);
          if (factor < 0) dir = -1;
          else if (factor > 0) dir = 1;
          move += dir * move * Math.abs(factor);
          base -= move;
          track.style.transform = `translate3d(${wrap(-100 / repeat, 0, base)}%,0,0)`;
        } else lastY = window.scrollY;
        requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    });
  }

  /* ------------------------------------------------------------------ steam */
  // Soft rising steam on a canvas (Steam.tsx). data-steam: [{"x":40,"y":60,"rate":9,"scale":1,"spread":1.2}]
  function steamBox(emitters) {
    const xs = emitters.map((e) => e.x);
    const ys = emitters.map((e) => e.y);
    const left = Math.max(0, Math.min(...xs) - 9);
    const right = Math.min(100, Math.max(...xs) + 9);
    const top = Math.max(0, Math.min(...ys) - 34);
    const bottom = Math.min(100, Math.max(...ys) + 4);
    return { left, top, width: right - left, height: bottom - top };
  }
  function initSteam(root = document) {
    $$("canvas[data-steam]", root).forEach((canvas) => {
      if (canvas.__steam) return;
      canvas.__steam = true;
      let emitters;
      try {
        emitters = JSON.parse(canvas.dataset.steam);
      } catch {
        return;
      }
      const area = steamBox(emitters);
      Object.assign(canvas.style, { left: `${area.left}%`, top: `${area.top}%`, width: `${area.width}%`, height: `${area.height}%` });
      if (reduceMotion()) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const color = canvas.dataset.color || "255,246,232";
      const intensity = parseFloat(canvas.dataset.intensity || "1");
      let w = 0, h = 0, fw = 0, fh = 0, raf = 0, visible = false, last = performance.now();
      const puffs = [];
      const SPRITE = 64;
      const sprite = document.createElement("canvas");
      sprite.width = sprite.height = SPRITE;
      const sctx = sprite.getContext("2d");
      const sg = sctx.createRadialGradient(SPRITE / 2, SPRITE / 2, 0, SPRITE / 2, SPRITE / 2, SPRITE / 2);
      sg.addColorStop(0, `rgba(${color},1)`);
      sg.addColorStop(1, `rgba(${color},0)`);
      sctx.fillStyle = sg;
      sctx.fillRect(0, 0, SPRITE, SPRITE);
      const acc = emitters.map(() => 0);
      const resize = () => {
        const r = canvas.getBoundingClientRect();
        w = r.width;
        h = r.height;
        fw = w / (area.width / 100);
        fh = h / (area.height / 100);
        canvas.width = Math.round(w);
        canvas.height = Math.round(h);
      };
      resize();
      new ResizeObserver(resize).observe(canvas);
      const tick = (now) => {
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        ctx.clearRect(0, 0, w, h);
        const unit = Math.min(fw, fh);
        const ox = (area.left / 100) * fw;
        const oy = (area.top / 100) * fh;
        emitters.forEach((em, i) => {
          acc[i] += dt * (em.rate || 9);
          while (acc[i] > 1) {
            acc[i] -= 1;
            const s = em.scale || 1;
            puffs.push({
              x: (em.x / 100) * fw - ox + (Math.random() - 0.5) * (em.spread || 1.2) * unit * 0.02,
              y: (em.y / 100) * fh - oy,
              vx: (Math.random() - 0.5) * 6,
              vy: -(14 + Math.random() * 16) * s,
              r: (4 + Math.random() * 6) * s * (unit / 600),
              life: 0,
              max: 3.2 + Math.random() * 2.2,
              seed: Math.random() * 100,
            });
          }
        });
        ctx.globalCompositeOperation = "screen";
        for (let i = puffs.length - 1; i >= 0; i--) {
          const p = puffs[i];
          p.life += dt;
          if (p.life > p.max) {
            puffs.splice(i, 1);
            continue;
          }
          const t = p.life / p.max;
          p.x += (p.vx + Math.sin(p.seed + p.life * 1.6) * 9) * dt;
          p.y += p.vy * dt;
          const r = p.r * (1 + t * 3.2);
          ctx.globalAlpha = Math.sin(Math.PI * t) * 0.075 * intensity;
          ctx.drawImage(sprite, p.x - r, p.y - r, r * 2, r * 2);
        }
        ctx.globalAlpha = 1;
        if (visible) raf = requestAnimationFrame(tick);
      };
      new IntersectionObserver(([e]) => {
        visible = e.isIntersecting;
        if (visible) {
          last = performance.now();
          raf = requestAnimationFrame(tick);
        } else cancelAnimationFrame(raf);
      }).observe(canvas);
    });
  }

  /* ------------------------------------------------------------------ header */
  function initHeader(root = document) {
    const header = root.querySelector("[data-header]");
    if (!header || header.__header) return;
    header.__header = true;
    const bar = header.querySelector("[data-header-bar]");
    const mega = header.querySelector("[data-mega]");
    const panels = $$("[data-mega-panel]", header);
    const items = $$("[data-world]", header);
    const pill = header.querySelector("[data-nav-pill]");
    const list = header.querySelector("[data-nav-list]");
    let menu = null;
    let scrolled = false;

    const paint = () => {
      const solid = scrolled || !!menu;
      bar.dataset.headerTop = String(!solid);
      bar.classList.toggle("is-solid", solid);
      if (list) list.classList.toggle("is-solid", solid);
    };
    // The sliding highlight behind the hovered / current item (motion layoutId="nav-pill").
    const movePill = (li) => {
      if (!pill || !list) return;
      if (!li) {
        pill.style.opacity = "0";
        return;
      }
      const a = li.getBoundingClientRect();
      const b = list.getBoundingClientRect();
      pill.style.opacity = "1";
      pill.style.transform = `translateX(${a.left - b.left - 6}px)`;
      pill.style.width = `${a.width}px`;
    };
    const current = () => items.find((li) => li.dataset.current === "true");
    const setMenu = (name) => {
      menu = name;
      items.forEach((li) => {
        const on = li.dataset.world === name;
        const a = li.querySelector("a");
        a && a.setAttribute("aria-expanded", String(on));
        li.classList.toggle("is-hover", on);
      });
      const panel = panels.find((p) => p.dataset.megaPanel === name);
      panels.forEach((p) => (p.hidden = p !== panel));
      if (mega) mega.classList.toggle("is-open", !!panel);
      paint();
    };
    items.forEach((li) => {
      li.addEventListener("mouseenter", () => {
        setMenu(li.dataset.world);
        movePill(li);
      });
      const a = li.querySelector("a");
      a && a.addEventListener("focus", () => {
        setMenu(li.dataset.world);
        movePill(li);
      });
    });
    $$("[data-mega-close]", header).forEach((el) => el.addEventListener("mouseenter", () => setMenu(null)));
    header.addEventListener("mouseleave", () => {
      setMenu(null);
      movePill(current());
    });
    header.addEventListener("focusout", (e) => {
      if (!header.contains(e.relatedTarget)) {
        setMenu(null);
        movePill(current());
      }
    });
    mega && mega.addEventListener("click", (e) => e.target.closest("a") && setMenu(null));

    const onScroll = () => {
      const s = window.scrollY > 24;
      if (s !== scrolled) {
        scrolled = s;
        paint();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    paint();
    requestAnimationFrame(() => movePill(current()));
    window.addEventListener("resize", () => movePill(current()));

    // Mobile menu
    const mobile = document.querySelector("[data-mobile-menu]");
    const mobileApi = makeOverlay(mobile);
    $$("[data-mobile-open]").forEach((b) => b.addEventListener("click", () => mobileApi && mobileApi.open()));
    mobile && mobile.addEventListener("click", (e) => e.target.closest("a") && mobileApi.close());
    const wide = window.matchMedia("(min-width: 1024px)");
    wide.addEventListener("change", () => wide.matches && mobileApi && mobileApi.close());
    document.addEventListener("keydown", (e) => e.key === "Escape" && setMenu(null));
  }

  /* ------------------------------------------------------------------ scroll buttons */
  function initScrollButtons() {
    const wrap = document.querySelector("[data-scroll-buttons]");
    if (!wrap || wrap.__init) return;
    wrap.__init = true;
    const up = wrap.querySelector("[data-scroll-top]");
    const down = wrap.querySelector("[data-scroll-bottom]");
    const go = (top) => window.scrollTo({ top, behavior: reduceMotion() ? "auto" : "smooth" });
    up.addEventListener("click", () => go(0));
    down.addEventListener("click", () => go(document.documentElement.scrollHeight));
    let frame = 0;
    const show = (el, on) => {
      el.classList.toggle("opacity-0", !on);
      el.classList.toggle("pointer-events-none", !on);
      el.tabIndex = on ? 0 : -1;
    };
    const measure = () => {
      frame = 0;
      const y = window.scrollY;
      const left = document.documentElement.scrollHeight - window.innerHeight - y;
      show(up, y > 320);
      show(down, left > 320);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    new ResizeObserver(schedule).observe(document.body);
  }

  /* ------------------------------------------------------------------ cart */
  // The Next.js cart lived in localStorage; here it is Shopify's own cart (AJAX API), and the
  // drawer is the cart-drawer section, re-rendered by Shopify after every change.
  const Cart = {
    drawer: null,
    sectionId: "cart-drawer",
    get el() {
      return document.querySelector("[data-cart-drawer]");
    },
    open() {
      this.drawer = makeOverlay(this.el);
      this.drawer && this.drawer.open();
    },
    close() {
      this.drawer && this.drawer.close();
    },
    render(sections) {
      const html = sections && sections[this.sectionId];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, "text/html");
      const next = doc.querySelector("[data-cart-drawer-body]");
      const cur = document.querySelector("[data-cart-drawer-body]");
      if (next && cur) cur.replaceWith(next);
      const count = doc.querySelector("[data-cart-drawer]")?.dataset.count;
      if (count !== undefined) this.setCount(parseInt(count, 10) || 0);
      initReveal(this.el || document);
    },
    setCount(n) {
      $$("[data-cart-count]").forEach((el) => {
        const was = el.textContent;
        el.textContent = String(n);
        el.hidden = n === 0;
        if (String(n) !== was && n) {
          el.classList.remove("pop-in");
          void el.offsetWidth;
          el.classList.add("pop-in");
        }
      });
      $$("[data-cart-label]").forEach((el) => el.setAttribute("aria-label", `Cart, ${n} item${n === 1 ? "" : "s"}`));
    },
    async request(url, body) {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...body, sections: this.sectionId, sections_url: window.location.pathname }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.status) throw new Error(json.description || json.message || "Something went wrong. Please try again.");
      return json;
    },
    async add(items, { open = true } = {}) {
      const json = await this.request(`${routes.cartAdd}.js`, { items });
      this.render(json.sections);
      document.dispatchEvent(new CustomEvent("champions:cart-updated", { detail: json }));
      if (open) this.open();
      return json;
    },
    async change(line, quantity) {
      const body = this.el && this.el.querySelector("[data-cart-drawer-body]");
      body && body.setAttribute("aria-busy", "true");
      try {
        const json = await this.request(`${routes.cartChange}.js`, { line, quantity });
        this.render(json.sections);
        document.dispatchEvent(new CustomEvent("champions:cart-updated", { detail: json }));
      } catch (err) {
        const msg = this.el && this.el.querySelector("[data-cart-error]");
        if (msg) msg.textContent = err.message;
      } finally {
        const b = this.el && this.el.querySelector("[data-cart-drawer-body]");
        b && b.removeAttribute("aria-busy");
      }
    },
  };

  /* A short message at the bottom of the screen (e.g. "Only 2 left"), instead of alert(). */
  function toast(message) {
    let el = document.getElementById("champions-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "champions-toast";
      el.setAttribute("role", "status");
      el.className = "scope-dark fixed inset-x-4 bottom-5 z-[80] mx-auto max-w-md rounded-2xl border border-gold/40 bg-cosmos-night px-5 py-3 text-center text-sm text-ivory shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)] transition-opacity duration-300";
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.opacity = "1";
    clearTimeout(el.__t);
    el.__t = setTimeout(() => (el.style.opacity = "0"), 4000);
  }

  function initCart(root = document) {
    if (!document.__cartInit) {
      document.__cartInit = true;
      document.addEventListener("click", (e) => {
        const opener = e.target.closest("[data-cart-open]");
        if (opener && Cart.el) {
          e.preventDefault();
          Cart.open();
          return;
        }
        const qty = e.target.closest("[data-cart-qty]");
        if (qty) {
          e.preventDefault();
          Cart.change(parseInt(qty.dataset.line, 10), Math.max(0, Math.min(parseInt(qty.dataset.qty, 10), 20)));
          return;
        }
        const quick = e.target.closest("[data-quick-add]");
        if (quick) {
          e.preventDefault();
          if (quick.disabled) return;
          quick.setAttribute("aria-busy", "true");
          Cart.add([{ id: Number(quick.dataset.variantId), quantity: Number(quick.dataset.quantity || 1) }])
            .catch((err) => toast(err.message))
            .finally(() => quick.removeAttribute("aria-busy"));
        }
      });
      // Coming back with the Back button from checkout restores the page as it was left.
      window.addEventListener("pageshow", (e) => {
        if (!e.persisted) return;
        $$("[data-checkout-button]").forEach((b) => {
          b.disabled = false;
          b.textContent = b.dataset.label || "Checkout";
        });
      });
      document.addEventListener("submit", (e) => {
        const f = e.target.closest("[data-checkout-form]");
        if (!f) return;
        const b = f.querySelector("[data-checkout-button]");
        if (b) {
          b.dataset.label = b.textContent;
          b.textContent = "Taking you to checkout…";
        }
      });
    }
    $$("form[data-product-form]", root).forEach((form) => {
      if (form.__init) return;
      form.__init = true;
      form.addEventListener("submit", async (e) => {
        if (!Cart.el) return; // no drawer: let the form post to /cart/add normally
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const err = form.querySelector("[data-product-error]");
        const fd = new FormData(form);
        const id = Number(fd.get("id"));
        const quantity = Math.max(1, Number(fd.get("quantity") || 1));
        if (err) err.textContent = "";
        btn && btn.setAttribute("aria-busy", "true");
        try {
          const json = await Cart.add([{ id, quantity }]);
          form.dispatchEvent(new CustomEvent("champions:product-added", { bubbles: true, detail: json }));
        } catch (ex) {
          if (err) err.textContent = ex.message;
        } finally {
          btn && btn.removeAttribute("aria-busy");
        }
      });
    });
  }

  /* ------------------------------------------------------------------ search */
  // Header search (SearchDialog.tsx): Shopify predictive search for products, pages, articles and
  // collections, plus the theme's own index of site pages and brands (snippet search-dialog).
  function initSearch() {
    const dialog = document.querySelector("[data-search-dialog]");
    if (!dialog || dialog.__init) return;
    dialog.__init = true;
    const api = makeOverlay(dialog, { onClose: () => {} });
    const input = dialog.querySelector("[data-search-input]");
    const results = dialog.querySelector("[data-search-results]");
    const idle = dialog.querySelector("[data-search-idle]");
    let local = [];
    try {
      local = JSON.parse(dialog.querySelector("[data-search-index]").textContent);
    } catch {}
    let active = -1;
    let current = [];
    let timer = 0;
    let ctl;

    const open = () => {
      api.open();
      setTimeout(() => input.focus(), 40);
    };
    $$("[data-search-open]").forEach((b) => b.addEventListener("click", open));
    window.addEventListener("keydown", (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        open();
      }
    });

    const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
    const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const searchLocal = (q) => {
      const words = norm(q).split(/\s+/).filter(Boolean);
      return local
        .map((it) => {
          const hay = norm(`${it.title} ${it.detail} ${it.keywords || ""}`);
          const t = norm(it.title);
          let score = 0;
          for (const w of words) {
            if (!hay.includes(w)) return null;
            score += t.startsWith(w) ? 6 : t.includes(w) ? 4 : 1;
          }
          return { ...it, score };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);
    };
    const paint = (q) => {
      idle.hidden = !!q;
      results.hidden = !q;
      if (!q) return;
      if (!current.length) {
        results.innerHTML = `<div class="p-4"><p class="font-medium">No results for &ldquo;${esc(q)}&rdquo;</p><p class="mt-1 text-sm text-[#5d5243]">Check the spelling, try a shorter word, or start from one of the suggestions.</p></div>`;
        return;
      }
      results.innerHTML =
        `<ul role="listbox" aria-label="Suggestions">` +
        current
          .map(
            (r, i) => `<li id="search-result-${i}" role="option" aria-selected="${active === i}">
              <a href="${esc(r.href)}" ${r.external ? 'target="_blank" rel="noopener noreferrer"' : ""} data-i="${i}" class="flex w-full items-center justify-between gap-4 rounded-2xl px-4 py-3 text-left transition-colors ${active === i ? "bg-[#efe6d6]" : ""}">
                <span class="flex min-w-0 items-center gap-3">${r.image ? `<img src="${esc(r.image)}" alt="" width="40" height="40" class="h-10 w-10 shrink-0 rounded-lg object-cover">` : ""}<span class="min-w-0"><span class="block truncate font-medium">${esc(r.title)}</span><span class="block truncate text-sm text-[#5d5243]">${esc(r.detail)}</span></span></span>
                <span class="flex shrink-0 items-center gap-2 text-[0.6rem] uppercase tracking-[0.16em] text-[#8c7a5c]">${esc(r.kind)} &rarr;</span>
              </a></li>`,
          )
          .join("") +
        `<li class="px-4 pb-2 pt-3"><a href="${routes.search}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last" class="inline-flex items-center gap-2 text-sm font-medium text-[#9a7a38] hover:underline">See all results for &ldquo;${esc(q)}&rdquo; &rarr;</a></li></ul>`;
      results.querySelectorAll("a[data-i]").forEach((a) => {
  a.addEventListener("mouseenter", () => {
    active = Number(a.dataset.i);

    results.querySelectorAll("a[data-i]").forEach((item) => {
      const isActive = Number(item.dataset.i) === active;

      item.classList.toggle("bg-[#efe6d6]", isActive);
      item.closest("li")?.setAttribute("aria-selected", String(isActive));
    });
  });
});
    };
    const strip = (html) => {
      const d = document.createElement("div");
      d.innerHTML = html || "";
      return (d.textContent || "").replace(/\s+/g, " ").trim();
    };
    const run = async (q) => {
      active = -1;
      const mine = searchLocal(q);
      current = mine;
      paint(q);
      if (ctl) ctl.abort();
      ctl = new AbortController();
      try {
        const url = `${routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product,page,article,collection&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor,tag,body`;
        const res = await fetch(url, { signal: ctl.signal });
        const json = await res.json();
        const r = json.resources && json.resources.results ? json.resources.results : {};
        const shop = [
          ...(r.products || []).map((p) => ({ title: p.title, detail: p.product_type || strip(p.body).slice(0, 90), href: p.url, kind: "Coffee", image: p.featured_image && p.featured_image.url ? `${p.featured_image.url}${p.featured_image.url.includes("?") ? "&" : "?"}width=80` : "" })),
          ...(r.collections || []).map((c) => ({ title: c.title, detail: "Collection", href: c.url, kind: "Collection" })),
          ...(r.pages || []).map((p) => ({ title: p.title, detail: strip(p.body).slice(0, 90), href: p.url, kind: "Page" })),
          ...(r.articles || []).map((a) => ({ title: a.title, detail: strip(a.summary_html || a.body).slice(0, 90), href: a.url, kind: "Journal" })),
        ];
        const seen = new Set(shop.map((s) => s.href.split("?")[0]));
        current = [...shop, ...mine.filter((m) => !seen.has(m.href))].slice(0, 12);
        paint(q);
      } catch (e) {
        if (e.name !== "AbortError") paint(q);
      }
    };
    input.addEventListener("input", () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) {
        current = [];
        paint("");
        return;
      }
      timer = setTimeout(() => run(q), 120);
    });
    input.addEventListener("keydown", (e) => {
      const q = input.value.trim();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        active = Math.min(current.length - 1, active + 1);
        paint(q);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        active = Math.max(-1, active - 1);
        paint(q);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (active >= 0 && current[active]) {
          const r = current[active];
          if (r.external) window.open(r.href, "_blank", "noopener,noreferrer");
          else window.location.href = r.href;
        } else if (q) window.location.href = `${routes.search}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
      }
    });
    dialog.addEventListener("mousedown", (e) => {
      if (e.target === dialog) api.close();
    });
  }

  /* ------------------------------------------------------------------ lead forms */
  /*
   * The site's forms (tasting, enquiries, Be a Champion, newsletter, Find Your Coffee) are stored in
   * Shopify instead of a sheet or a server: each submission becomes (or updates) a Customer, tagged
   * with the form ("lead-tasting", "lead-enquiry-office"...) and the answers, with every answer also
   * written to the customer note. Admin > Customers > filter by tag > Export gives a CSV.
   *
   * Markup (snippet "lead-form-open"): a Shopify {% form 'customer' %} with data-lead-form="<kind>".
   *   name="contact[email]"         required, the customer's email
   *   name="contact[first_name]"    optional; data-full-name on a single "Name" field splits it
   *   data-lead-field="Label"       any answer: stored in the note as "Label: value"
   *   data-lead-tag="prefix"        also stored as the tag "prefix:value"
   *   data-required + [data-error-for="<input name>"]   validation messages
   *   [data-lead-success] / [data-lead-fields]           what to show after / before sending
   *
   * Sending: the answers are written into the form as Shopify's own field names (writeLead), then
   * the form goes through Shopify's hCaptcha and is posted once with fetch, so the visitor stays on
   * the page (submitLead / sendLead). If Shopify answers with its bot challenge, the filled-in form
   * is posted normally instead: the visitor completes the challenge and comes back to the page,
   * where Liquid shows the success state.
   */
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE = /^[+\d][\d\s()-]{6,}$/;
  const tagValue = (v) => String(v).replace(/[,\n\r]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 240);
  const newReference = (prefix) => `${prefix}-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  function fieldValue(el, form) {
    if (el.type === "checkbox") {
      const group = $$(`input[type="checkbox"][name="${CSS.escape(el.name)}"]`, form);
      if (group.length > 1) return group.filter((c) => c.checked).map((c) => c.value);
      return el.checked ? el.value || "Yes" : el.dataset.unchecked || "No";
    }
    if (el.type === "radio") {
      const on = $$(`input[type="radio"][name="${CSS.escape(el.name)}"]`, form).find((r) => r.checked);
      return on ? on.value : "";
    }
    return el.value.trim();
  }

  function validateLead(form) {
    const errors = {};
    $$("[data-required], [required]", form).forEach((el) => {
      if (el.disabled || el.closest("[hidden]")) return;
      const v = fieldValue(el, form);
      const empty = Array.isArray(v) ? !v.length : !v || (el.type === "checkbox" && !el.checked && !el.dataset.unchecked);
      const kind = el.dataset.validate || (el.type === "email" ? "email" : el.type === "tel" ? "phone" : "");
      let bad = empty;
      if (!empty && kind === "email") bad = !EMAIL.test(v);
      if (!empty && kind === "phone") bad = !PHONE.test(v);
      if (bad && !errors[el.name]) errors[el.name] = el.dataset.error || "Please fill this in.";
    });
    // optional email/phone fields that are filled in must still be valid
    $$('input[type="email"], input[type="tel"]', form).forEach((el) => {
      const v = el.value.trim();
      if (!v || errors[el.name]) return;
      if (el.type === "email" && !EMAIL.test(v)) errors[el.name] = el.dataset.error || "Please add a valid email.";
      if (el.type === "tel" && !PHONE.test(v)) errors[el.name] = el.dataset.error || "Please add a valid phone number.";
    });
    return errors;
  }

  function showErrors(form, errors) {
    $$("[data-error-for]", form).forEach((el) => {
      const msg = errors[el.dataset.errorFor] || "";
      el.textContent = msg;
      el.hidden = !msg;
    });
    $$("[name]", form).forEach((el) => {
      if (el.type === "hidden") return;
      if (errors[el.name]) el.setAttribute("aria-invalid", "true");
      else el.removeAttribute("aria-invalid");
    });
    const first = Object.keys(errors)[0];
    if (first) {
      const el = form.querySelector(`[name="${CSS.escape(first)}"]`);
      el && el.focus();
    }
  }

  function collectLead(form) {
    const kind = form.dataset.leadForm;
    const reference = newReference(form.dataset.leadPrefix || kind.slice(0, 2).toUpperCase());
    const answers = [];
    const tags = ["lead", `lead-${kind}`, ...(form.dataset.leadTags || "").split(",").map((t) => t.trim()).filter(Boolean)];
    $$("[data-lead-field]", form).forEach((el) => {
      if (el.disabled || el.closest("[hidden]")) return;
      if ((el.type === "checkbox" || el.type === "radio") && answers.some((a) => a.name === el.name)) return;
      const v = fieldValue(el, form);
      const value = Array.isArray(v) ? v.join(", ") : v;
      if (!value) return;
      answers.push({ name: el.name, label: el.dataset.leadField, key: el.dataset.leadKey, value, tagged: !!el.dataset.leadTag || el.matches("[data-lead-notag], [data-full-name], [data-marketing-consent]") });
      if (el.dataset.leadTag) (Array.isArray(v) ? v : [v]).forEach((x) => tags.push(`${el.dataset.leadTag}:${tagValue(x)}`));
    });
    const consent = form.querySelector("[data-marketing-consent]");
    if (consent) tags.push(consent.checked ? "marketing:yes" : "marketing:no");
    tags.push(`ref:${reference}`);
    const email = form.querySelector('[name="contact[email]"]').value.trim();
    return { kind, reference, answers, email, tags: [...new Set(tags.map(tagValue).filter(Boolean))] };
  }

  /*
   * Writes the submission into the form itself as Shopify's own field names, so a single normal
   * post carries everything: contact[tags], contact[first_name]/[last_name], contact[note][Label]
   * and contact[accepts_marketing]. Without the note (an email that already belongs to a customer:
   * Shopify keeps that customer's note), every answer is also written as a tag, so a returning
   * visitor's answers are still on their customer record.
   */
  function writeLead(form, lead, { withNote }) {
    $$("[data-lead-generated]", form).forEach((el) => el.remove());
    const add = (name, value) => {
      const h = document.createElement("input");
      h.type = "hidden";
      h.name = name;
      h.value = value;
      h.dataset.leadGenerated = "";
      form.appendChild(h);
    };
    const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const tags = [...lead.tags];
    if (!withNote) {
      const prefix = slug(form.dataset.leadPrefix || lead.kind);
      lead.answers.forEach((a) => {
        if (a.tagged) return;
        const v = tagValue(a.value);
        tags.push(`${prefix}-${slug(a.key || a.label)}:${v.length > 200 ? `${v.slice(0, 199)}…` : v}`);
      });
    }
    let tagsInput = form.querySelector('input[type="hidden"][name="contact[tags]"]');
    if (!tagsInput) {
      add("contact[tags]", "");
      tagsInput = form.querySelector('[name="contact[tags]"][data-lead-generated]');
    }
    tagsInput.value = [...new Set(tags)].join(", ");

    const nameEl = form.querySelector("[data-full-name]");
    if (nameEl && nameEl.value.trim() && !form.querySelector('[name="contact[first_name]"]:not([data-lead-generated])')) {
      const parts = nameEl.value.trim().split(/\s+/);
      add("contact[first_name]", parts.shift());
      if (parts.length) add("contact[last_name]", parts.join(" "));
    }
    const consent = form.querySelector("[data-marketing-consent]");
    if (consent) add("contact[accepts_marketing]", consent.checked ? "true" : "false");
    if (withNote) {
      add("contact[note][Form]", `${form.dataset.leadLabel || lead.kind} (${lead.reference})`);
      add("contact[note][Submitted]", new Date().toISOString().slice(0, 16).replace("T", " ") + " UTC");
      lead.answers.forEach((a) => add(`contact[note][${a.label}]`, a.value.slice(0, 1500)));
    }
  }

  /** Reads Shopify's answer to a post: saved, a bot challenge, or the form's error message. */
  async function readLeadResponse(res, form) {
    const html = await res.text();
    if (/\/challenge/.test(res.url)) return { challenge: true };
    if (res.url.includes("customer_posted=true") || /data-lead-posted/.test(html)) return { ok: true };
    const doc = new DOMParser().parseFromString(html, "text/html");
    const err = (form.id && doc.querySelector(`#${CSS.escape(form.id)} [data-lead-server-errors]`)) || doc.querySelector("[data-lead-server-errors]");
    return { ok: false, text: err ? err.textContent.replace(/\s+/g, " ").trim() : "" };
  }

  /*
   * Shopify protects customer and contact forms with an invisible hCaptcha. Its script binds to a
   * form (on first focus, or through Shopify.captcha.protect) and wraps form.submit: it runs the
   * check, adds the h-captcha-response field and then calls the form's own submit. Each lead form's
   * submit is set to `sendLead` before that happens, so the wrapped call ends here and the post,
   * token included, is sent with fetch: one post per submission, and the visitor stays on the page.
   */
  async function sendLead(form) {
    const done = form.__leadResolve;
    form.__leadResolve = null;
    if (!done) return HTMLFormElement.prototype.submit.call(form);
    window.clearTimeout(form.__leadTimer);
    try {
      const action = form.getAttribute("action") || "/contact";
      const res = await fetch(action, { method: "POST", body: new FormData(form), headers: { Accept: "text/html" }, credentials: "same-origin" });
      done(await readLeadResponse(res, form));
    } catch (e) {
      done({ ok: false, text: "" });
    }
  }

  const waitFor = (test, ms) =>
    new Promise((resolve) => {
      const start = Date.now();
      (function check() {
        if (test() || Date.now() - start > ms) return resolve();
        window.setTimeout(check, 100);
      })();
    });

  async function submitLead(form) {
    const captcha = window.Shopify && window.Shopify.captcha;
    if (captcha && captcha.protect && !form.dataset.hcaptchaBound) {
      if (!form.dataset.cptcha) captcha.protect(form, () => {});
      await waitFor(() => form.dataset.hcaptchaBound, 6000);
    }
    // Shopify's wrapped submit ignores a second call within 250 ms of the first (a resend).
    const since = Date.now() - (form.__leadSubmitted || 0);
    if (since < 400) await new Promise((r) => window.setTimeout(r, 400 - since));
    form.__leadSubmitted = Date.now();
    return new Promise((resolve) => {
      form.__leadResolve = resolve;
      // No answer from the check (blocked script, closed puzzle): hand over to a normal post.
      form.__leadTimer = window.setTimeout(() => {
        if (form.__leadResolve !== resolve) return;
        form.__leadResolve = null;
        resolve({ challenge: true });
      }, 45000);
      form.submit();
    });
  }

  function notifyByEmail(form, lead) {
    if (form.dataset.leadNotify !== "true") return;
    const f = document.createElement("form");
    f.method = "post";
    f.action = "/contact";
    f.hidden = true;
    f.setAttribute("accept-charset", "UTF-8");
    const add = (name, value) => {
      const h = document.createElement("input");
      h.type = "hidden";
      h.name = name;
      h.value = value;
      f.appendChild(h);
    };
    add("form_type", "contact");
    add("utf8", "✓");
    add("contact[email]", lead.email);
    add("contact[Form]", `${form.dataset.leadLabel || lead.kind} (${lead.reference})`);
    lead.answers.forEach((a) => add(`contact[${a.label}]`, a.value));
    add("contact[Tags]", lead.tags.join(", "));
    add("contact[body]", lead.answers.map((a) => `${a.label}: ${a.value}`).join("\n"));
    let sent = false;
    f.submit = () => {
      if (sent) return;
      sent = true;
      // Best effort: the customer record is the stored copy.
      fetch("/contact", { method: "POST", body: new FormData(f), credentials: "same-origin", keepalive: true })
        .catch(() => {})
        .finally(() => window.setTimeout(() => f.remove(), 2000));
    };
    document.body.appendChild(f);
    const captcha = window.Shopify && window.Shopify.captcha;
    if (captcha && captcha.protect) captcha.protect(f, () => f.submit());
    else f.submit();
  }

  async function onLeadSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches("form[data-lead-form]")) return;
    // First in line (capture on the document): Shopify's own submit handlers would otherwise post
    // the bare form at the same time, without the answers.
    e.preventDefault();
    e.stopImmediatePropagation();
    if (form.__busy) return; // duplicate-submit guard
    const status = form.querySelector("[data-lead-status]");
    const btn = form.querySelector('[type="submit"]');
    const errors = validateLead(form);
    showErrors(form, errors);
    if (Object.keys(errors).length) {
      if (status) status.textContent = "Please check the highlighted fields.";
      return;
    }
    const lead = collectLead(form);
    form.__busy = true;
    form.setAttribute("aria-busy", "true");
    if (btn) {
      btn.disabled = true;
      btn.dataset.label = btn.dataset.label || btn.innerHTML;
      btn.innerHTML = `<span class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true"></span> ${btn.dataset.busyLabel || "Sending…"}`;
    }
    if (status) status.textContent = "";
    let leaving = false;
    try {
      let withNote = true;
      writeLead(form, lead, { withNote });
      let r = await submitLead(form);
      // An email that already belongs to a customer rejects the note ("Note is invalid"): send
      // again with the answers as tags (Shopify adds tags to the existing customer).
      if (!r.ok && !r.challenge && /note/i.test(r.text || "")) {
        withNote = false;
        writeLead(form, lead, { withNote });
        r = await submitLead(form);
      }
      if (r.challenge) {
        // Shopify wants the visitor to complete its check: post the filled-in form normally; the
        // visitor comes back to this page and Liquid shows the confirmation.
        leaving = true;
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      if (!r.ok) throw new Error(r.text || "");
      notifyByEmail(form, lead);
      const rootEl = form.closest("[data-lead-root]") || form;
      $$("[data-lead-reference]", rootEl).forEach((el) => (el.textContent = lead.reference));
      $$("[data-lead-email]", rootEl).forEach((el) => (el.textContent = lead.email));
      $$("[data-lead-fields]", rootEl).forEach((el) => (el.hidden = true));
      $$("[data-lead-success]", rootEl).forEach((el) => {
        el.hidden = false;
        initReveal(el);
      });
      form.dispatchEvent(new CustomEvent("champions:lead", { bubbles: true, detail: lead }));
      const focus = rootEl.querySelector("[data-lead-success] [tabindex], [data-lead-success] h2, [data-lead-success] h3, [data-lead-success] p");
      if (focus) {
        focus.setAttribute("tabindex", "-1");
        focus.focus();
      }
    } catch (err) {
      if (status) status.textContent = err.message && err.message.length < 200 ? err.message : form.dataset.failMessage || "We could not send that just now. Please try again in a moment.";
    } finally {
      if (!leaving) {
        form.__busy = false;
        form.removeAttribute("aria-busy");
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = btn.dataset.label;
        }
      }
    }
  }

  function initLeadForms(root = document) {
    if (!document.__leadSubmit) {
      document.__leadSubmit = true;
      document.addEventListener("submit", onLeadSubmit, true);
    }
    $$("form[data-lead-form]", root).forEach((form) => {
      if (form.__lead) return;
      form.__lead = true;
      form.submit = () => sendLead(form);
      form.addEventListener("input", (e) => {
        const el = e.target;
        if (el.getAttribute("aria-invalid") === "true") {
          el.removeAttribute("aria-invalid");
          const msg = form.querySelector(`[data-error-for="${CSS.escape(el.name)}"]`);
          if (msg) {
            msg.textContent = "";
            msg.hidden = true;
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------------ carousels */
  function initNudge(root = document) {
    $$("[data-nudge]", root).forEach((b) => {
      if (b.__nudge) return;
      b.__nudge = true;
      b.addEventListener("click", () => {
        const track = document.getElementById(b.dataset.nudge);
        if (!track) return;
        const dir = Number(b.dataset.dir || 1);
        track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: reduceMotion() ? "auto" : "smooth" });
      });
    });
  }

  /* ------------------------------------------------------------------ boot */
  function init(root = document) {
    initReveal(root);
    initMarquee(root);
    initSteam(root);
    initCart(root);
    initLeadForms(root);
    initNudge(root);
  }

  window.Champions = Object.assign(window.Champions || {}, {
    cart: Cart,
    init,
    initReveal,
    makeOverlay,
    lockScroll,
    newReference,
    reduceMotion,
    toast,
  });

  const start = () => {
    initHeader();
    initScrollButtons();
    initSearch();
    init(document);
    document.documentElement.classList.add("is-ready");
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();

  // Theme editor: re-attach behaviour to a section after the merchant edits it.
  document.addEventListener("shopify:section:load", (e) => {
    initHeader(e.target);
    init(e.target);
  });
  document.addEventListener("shopify:block:select", (e) => {
    $$("[data-reveal]", e.target).forEach((el) => el.classList.add("is-revealed"));
  });
})();
